import { isEmpty } from 'lodash';
import * as mux from '../../../components/video-library/mux';
import * as enums from '@client/enums';
import type { MuxVideo } from '@client/new/common/videos/videos.helpers';

const RENDITIONS = ['high', 'medium', 'low'] as const;

function expectedRendition(width: number) {
  if (width < 480) return 'low';
  if (width < 720) return 'medium';
  return 'high';
}

// Mux decides which MP4 renditions to create from the source, so a 640 px
// video can finish with only `low`. Once processing is finished, any
// rendition is the final answer.
export function isUploadedVideoReady(video: MuxVideo | undefined): boolean {
  if (!video?.video) return false;

  const links = video.downloadLink;
  if (!isEmpty(links?.[expectedRendition(video.width)])) return true;

  return (
    video.status === 'PROCESSING_FINISHED' &&
    RENDITIONS.some((rendition) => !isEmpty(links?.[rendition]))
  );
}

export async function influencerMuxVideoUpload(
  influencerId: number,
  file: File,
  config: {
    thumbnailPosition?: number;
    onProgress?: (progress: number) => void;
  } = {},
): Promise<MuxVideo> {
  const { thumbnailPosition, onProgress } = config ?? {};

  const data = await mux.generateMuxSignedUrl({
    influencerId,
    fileSize: file.size,
    title: file.name,
    thumbnailPosition: thumbnailPosition ?? 0,
    bucketName: enums.MUX_BUCKETS.LANDING_PAGE_BUILDER,
  });
  const videoId = data.video.id;
  const muxUploadInstance = mux.upload({
    file: file,
    url: data && data.signedURL,
    chunkSize: 5120,
    onProgress: (progress: number) => {
      onProgress && onProgress(progress);
    },
  });

  return new Promise((resolve, reject) => {
    muxUploadInstance.on('success', async () => {
      try {
        const videoResponse = await polling(
          1000,
          (r) => isUploadedVideoReady(r.video),
          async () => await mux.fetchVideo(videoId),
        );

        if (!videoResponse) {
          throw new Error(`Mux video ${videoId} was not ready in time`);
        }

        resolve(videoResponse.video);
      } catch (e) {
        reject(e);
      }
    });
    muxUploadInstance.on('error', (e: unknown) => {
      console.error('Error uploading influencer video to mux', e);
      reject(e);
    });
  });
}

async function polling<T>(
  timeout: number,
  isSuccessDef: (x: T) => boolean,
  asyncFn: () => Promise<T>,
) {
  let maxTries = 1000;
  while (maxTries > 0) {
    const result = await asyncFn();

    const isSuccess = isSuccessDef(result);

    if (isSuccess) {
      return result;
    }

    await sleep(timeout);
    maxTries -= 1;
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
